import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Chip, Grid, InputAdornment, Paper, TextField } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ExportMenu from '../../Shared/ExportMenu'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { getEmployeeService } from '../../../services/configServices'
import { mensagemDeErro } from '../../../services/apiClient'
import { formatDateTime } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'

const getName = row => row.fullName || row.name || ''
const getEmail = row => row.email || ''

const Users = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [rows, setRows] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  useDebounce(
    () => {
      setSearchTerm(search)
      setPage(0)
    },
    500,
    [search]
  )

  const fetchData = useCallback(async () => {
    if (!subCompanyId) {
      setRows([])
      return
    }
    setLoading(true)
    try {
      const response = await getEmployeeService(subCompanyId)
      setRows(response.data.result || [])
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar os usuários.'), 'error')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [subCompanyId, notify])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const visibleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return rows
    return rows.filter(
      row => getName(row).toLowerCase().includes(term) || getEmail(row).toLowerCase().includes(term)
    )
  }, [rows, searchTerm])

  const pagedRows = useMemo(
    () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visibleRows, page, rowsPerPage]
  )

  const columns = [
    { id: 'name', label: 'Nome', element: row => getName(row) || '—' },
    { id: 'email', label: 'E-mail', element: row => getEmail(row) || '—' },
    {
      id: 'roles',
      label: 'Roles',
      element: row => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(row.roles || []).length === 0
            ? '—'
            : row.roles.map(role => <Chip size="small" label={role} key={role} />)}
        </Box>
      ),
    },
    { id: 'created', label: 'Criado', element: row => formatDateTime(row.createdAt) },
  ]

  const exportColumns = [
    { header: 'Nome', accessor: row => getName(row) },
    { header: 'E-mail', accessor: row => getEmail(row) },
    { header: 'Roles', accessor: row => (row.roles || []).join(', ') },
    { header: 'Criado', accessor: row => formatDateTime(row.createdAt) },
  ]

  return (
    <Box>
      <PageHeader
        title="Usuários"
        subtitle="Usuários com acesso à filial, conforme o perfil do SSO"
        actions={
          <ExportMenu
            rows={visibleRows}
            columns={exportColumns}
            fileName="usuarios"
            title="Usuários"
          />
        }
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        Cadastro, bloqueio e redefinição de senha acontecem no painel do SSO Bihands. Esta tela é
        somente leitura para não manter um cadastro paralelo.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => {
                setSubCompanyId(event.target.value)
                setPage(0)
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Pesquisar"
              placeholder="Nome ou e-mail"
              value={search}
              onChange={event => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        columns={columns}
        rows={pagedRows}
        loading={loading}
        emptyMessage={
          subCompanyId
            ? 'Nenhum usuário encontrado.'
            : 'Selecione uma filial para listar os usuários.'
        }
        page={page}
        rowsPerPage={rowsPerPage}
        total={visibleRows.length}
        rowKey={row => row.userId || row.id || getEmail(row)}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default Users
